import type { Route } from '../types/routeTypes'
import type { UseGrowthBook } from '../utils/growthbook/generated'
import { getRouteConfig } from '../utils/routes'

type FilterRoutesByConfigArgs = {
  routes: Route<string>[]
  currentPath: string
  funnelId: string | undefined
  evalFeature: UseGrowthBook['evalFeature']
}

/**
 * Filters funnel routes by their GrowthBook config flag, but only resolves the
 * flag where it is relevant to the user's current position:
 *
 * - Routes up to and including the current page ("previous"): every route with
 *   a resolved flag is evaluated.
 * - Following routes: evaluated until the first route that is guaranteed to be
 *   present — one with no `condition` whose flag is on (or that resolves to no
 *   flag at all). That route becomes an anchor: it and everything after it are
 *   kept without further flag lookups. A following route with a `condition` can
 *   be skipped at runtime, so it never acts as an anchor, and a no-condition
 *   route whose flag is off is excluded while scanning continues.
 *
 * When `currentPath` is not found, no flag is evaluated and every route is
 * kept as-is.
 */
export const filterRoutesByConfig = ({
  routes,
  currentPath,
  funnelId,
  evalFeature,
}: FilterRoutesByConfigArgs): Route<string>[] => {
  const currentIndex = routes.findIndex((route) => route.path === currentPath)

  // Current page is unknown (e.g. router not ready): defer all flag evaluation.
  if (currentIndex === -1) {
    return routes
  }

  let reachedAnchor = false

  return routes.filter((route, index) => {
    // Stop evaluating once an anchor (a following, guaranteed-present route) is
    // reached. Keep it and everything after it.
    if (reachedAnchor) {
      return true
    }

    const evaluate = () => {
      if (!route.path) {
        return true
      }

      const config = getRouteConfig({ id: route.id, funnelId, evalFeature })
      return config ? config.isOn : true
    }

    // Previous routes + current page => always evaluate the flag.
    if (index <= currentIndex) {
      return evaluate()
    }

    // A following route with a condition may be skipped at runtime, so it can
    // never act as an anchor. Still evaluate its flag for filtering.
    if (route.condition) {
      return evaluate()
    }

    // First following route with no condition whose flag is on (or that has no
    // flag) is guaranteed to be present: it becomes the anchor. When its flag
    // is off it is excluded and scanning continues to the next one.
    const isOn = evaluate()
    if (isOn) {
      reachedAnchor = true
    }
    return isOn
  })
}
